import { useCallback, useMemo } from 'react';
import { useLocation } from '@reach/router';
import { removeLocalesFromPath } from './language';
import { useSearchParameter } from './useSearchParameter';

/**
 * Custom hook to retrieve the redirect URL from the current location's search parameters.
 *
 * @returns {string} The decoded redirect URL or an empty string if not found.
 */
export const useRedirectUrl = (): { redirectUrl: string; getPathWithRedirectUrl: (path: string) => string } => {
  const location = useLocation();
  const redirectUrl = useSearchParameter('redirectUrl');

  const currentRedirectUrl = useMemo(() => {
    return redirectUrl ? decodeURIComponent(redirectUrl) : '';
  }, [redirectUrl]);

  const getPathWithRedirectUrl = useCallback(
    (path: string) => {
      const newRedirectUrl = removeLocalesFromPath(location.pathname + location.search);
      const url = new URL(path, location.origin);
      const existingParams = new URLSearchParams(url.search);

      existingParams.set('redirectUrl', newRedirectUrl);

      return `${url.pathname}?${existingParams.toString()}`;
    },
    [location]
  );

  return {
    redirectUrl: currentRedirectUrl,
    getPathWithRedirectUrl
  };
};
